import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Modal, ModalBody, ModalHeader } from "reactstrap";

import { stringToMask } from "helpers/string";

import {
	createObservation,
	getBenefitDetails,
	updateDit,
	updateLifeInsurance
} from "../../actions";
import {
	BENEFIT_FORM_PARTNER_OPTIONS,
	BENEFIT_FORM_STATUS_OPTIONS,
	BENEFIT_STATUS,
	getStatusMeta
} from "../../constants";
import {
	Divider,
	EmptyState,
	Field,
	FieldsRow,
	FormBody,
	FullField,
	HeaderContent,
	ModalContent,
	ModalGlobalStyles,
	Observation,
	ObservationsBody,
	PrimaryButton,
	ProviderField,
	ProviderGrid,
	ProviderHeader,
	Section,
	SectionActions,
	SectionHeader,
	StatusBadge
} from "./styles";

const toInputDate = value => (value ? dayjs(value).format("YYYY-MM-DD") : "");
const toDisplayDate = value =>
	value ? dayjs(value).format("DD/MM/YYYY") : "—";

const updatedByName = user => {
	if (!user) return "Sistema";
	if (typeof user === "string") return user;
	return `${user.name || ""} ${user.lastname || ""}`.trim() || "Sistema";
};

export const updateFormField = (setForm, field, event) => {
	const value = event.target.value;

	setForm(current => ({
		...current,
		[field]: value
	}));
};

const BenefitForm = ({
	title,
	data,
	form,
	setForm,
	onSubmit,
	loading,
	isDit = false,
	partnerOptions
}) => {
	const status = getStatusMeta(data?.status);
	const isRejected = isDit && Number(form.status) === BENEFIT_STATUS.REJECTED;
	const changeField = field => event => updateFormField(setForm, field, event);

	return (
		<Section>
			<SectionHeader>
				<div>
					<h3>{title}</h3>
					<p>
						{data?.updatedAt
							? `Atualizado em ${toDisplayDate(
									data.updatedAt
							  )} por ${updatedByName(data.updatedBy)}`
							: "Ainda não atualizado"}
					</p>
				</div>
				<StatusBadge color={status.color}>{status.label}</StatusBadge>
			</SectionHeader>
			<FormBody>
				<FieldsRow>
					<Field>
						Parceiro
						<select
							value={form.partner}
							onChange={changeField("partner")}
							disabled={loading}
						>
							{partnerOptions.map(option => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</Field>
					{!isDit && (
						<Field>
							Acionamentos
							<input
								type="number"
								min="0"
								step="1"
								value={form.triggers}
								onChange={changeField("triggers")}
								disabled={loading}
							/>
						</Field>
					)}
				</FieldsRow>
				<FieldsRow>
					<Field>
						Status
						<select
							value={form.status}
							onChange={changeField("status")}
							disabled={loading}
						>
							{BENEFIT_FORM_STATUS_OPTIONS.map(option => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</Field>
					<Field>
						Data de liberação
						<input
							type="date"
							value={form.releasedAt}
							onChange={changeField("releasedAt")}
							disabled={loading}
						/>
					</Field>
				</FieldsRow>
				{isRejected && (
					<FullField>
						Motivo:
						<textarea
							value={form.rejectionReason}
							onChange={changeField("rejectionReason")}
							placeholder="Descreva o motivo."
							maxLength={2000}
							disabled={loading}
						/>
					</FullField>
				)}
				<SectionActions>
					<PrimaryButton type="button" onClick={onSubmit} disabled={loading}>
						{loading ? "Atualizando..." : `Atualizar ${title}`}
					</PrimaryButton>
				</SectionActions>
			</FormBody>
		</Section>
	);
};

const BenefitUpdateModal = ({ open, provider, token, onClose, onUpdated }) => {
	const [details, setDetails] = useState(null);
	const [loadingDetails, setLoadingDetails] = useState(false);
	const [updatingLife, setUpdatingLife] = useState(false);
	const [updatingDit, setUpdatingDit] = useState(false);
	const [savingObservation, setSavingObservation] = useState(false);
	const [observation, setObservation] = useState("");
	const [lifeForm, setLifeForm] = useState({
		status: String(BENEFIT_STATUS.PENDING),
		releasedAt: "",
		triggers: "0",
		partner: ""
	});
	const [ditForm, setDitForm] = useState({
		status: String(BENEFIT_STATUS.PENDING),
		releasedAt: "",
		rejectionReason: "",
		partner: ""
	});

	const loadDetails = async () => {
		if (!provider?._id) return;

		setLoadingDetails(true);
		try {
			const result = await getBenefitDetails(provider._id, token);
			setDetails(result);
			setLifeForm({
				status: String(result.lifeInsurance?.status ?? BENEFIT_STATUS.PENDING),
				releasedAt: toInputDate(result.lifeInsurance?.releasedAt),
				triggers: String(result.lifeInsurance?.triggers ?? 0),
				partner: result.lifeInsurance?.partner || ""
			});
			setDitForm({
				status: String(result.dit?.status ?? BENEFIT_STATUS.PENDING),
				releasedAt: toInputDate(result.dit?.releasedAt),
				rejectionReason: result.dit?.rejectionReason || "",
				partner: result.dit?.partner || ""
			});
		} catch (error) {
			toast.error(error.message);
		} finally {
			setLoadingDetails(false);
		}
	};

	useEffect(() => {
		if (open) {
			setObservation("");
			loadDetails();
		} else {
			setDetails(null);
		}
	}, [open, provider?._id]);

	const refreshAfterUpdate = async message => {
		toast.success(message);
		await loadDetails();
		onUpdated();
	};

	const handleLifeInsuranceUpdate = async () => {
		const triggers = Number(lifeForm.triggers);
		if (!Number.isInteger(triggers) || triggers < 0) {
			toast.warning("Informe um número válido de acionamentos.");
			return;
		}

		setUpdatingLife(true);
		try {
			await updateLifeInsurance(
				provider._id,
				{
					status: Number(lifeForm.status),
					releasedAt: lifeForm.releasedAt || null,
					triggers,
					partner: lifeForm.partner
				},
				token
			);
			await refreshAfterUpdate("Seguro de Vida atualizado com sucesso.");
		} catch (error) {
			toast.error(error.message);
		} finally {
			setUpdatingLife(false);
		}
	};

	const handleDitUpdate = async () => {
		if (
			Number(ditForm.status) === BENEFIT_STATUS.REJECTED &&
			!ditForm.rejectionReason.trim()
		) {
			toast.warning("Informe o motivo da rejeição da DIT.");
			return;
		}

		setUpdatingDit(true);
		try {
			await updateDit(
				provider._id,
				{
					status: Number(ditForm.status),
					releasedAt: ditForm.releasedAt || null,
					rejectionReason: ditForm.rejectionReason,
					partner: ditForm.partner
				},
				token
			);
			await refreshAfterUpdate("DIT atualizada com sucesso.");
		} catch (error) {
			toast.error(error.message);
		} finally {
			setUpdatingDit(false);
		}
	};

	const handleObservation = async () => {
		if (!observation.trim()) {
			toast.warning("Digite uma observação antes de salvar.");
			return;
		}

		setSavingObservation(true);
		try {
			await createObservation(provider._id, observation, token);
			setObservation("");
			await refreshAfterUpdate("Observação salva com sucesso.");
		} catch (error) {
			toast.error(error.message);
		} finally {
			setSavingObservation(false);
		}
	};

	const providerData = details?.provider;
	const providerName =
		providerData?.name?.trim() ||
		provider?.name?.trim() ||
		"Nome não informado";
	const providerCpf = providerData?.cpf || provider?.cpf;
	const observations = details?.observations || [];

	return (
		<>
			<ModalGlobalStyles />
			<Modal
				isOpen={open}
				toggle={onClose}
				className="benefit-update-modal"
				backdrop="static"
				size="sm"
			>
				<ModalHeader toggle={onClose}>
					<HeaderContent>
						<h2>Atualizar Benefícios</h2>
						<p>
							{providerName}
							{" · CPF "}
							{providerCpf ? stringToMask(providerCpf, "###.###.###-##") : "—"}
						</p>
					</HeaderContent>
				</ModalHeader>

				<ModalBody className="theme-light">
					{loadingDetails && !details ? (
						<EmptyState>Carregando dados do prestador...</EmptyState>
					) : details ? (
						<ModalContent>
							<Section>
								<ProviderHeader>Dados do prestador</ProviderHeader>
								<ProviderGrid>
									<ProviderField>
										<span>Nome</span>
										<strong>{providerName}</strong>
									</ProviderField>
									<ProviderField>
										<span>Empresa / CNPJ</span>
										<strong>
											{providerData?.company
												? `${providerData.company.name || "Empresa sem nome"}${
														providerData.company.cnpj
															? ` · ${stringToMask(
																	providerData.company.cnpj,
																	"##.###.###/####-##"
															  )}`
															: ""
												  }`
												: "Sem empresa vinculada"}
										</strong>
									</ProviderField>
									<ProviderField>
										<span>CPF</span>
										<strong>
											{providerData?.cpf
												? stringToMask(providerData.cpf, "###.###.###-##")
												: "—"}
										</strong>
									</ProviderField>
									<ProviderField>
										<span>Cadastrado em</span>
										<strong>{toDisplayDate(providerData?.createdAt)}</strong>
									</ProviderField>
									<ProviderField>
										<span>Data de ativação</span>
										<strong>{toDisplayDate(providerData?.activatedAt)}</strong>
									</ProviderField>
								</ProviderGrid>
							</Section>

							<BenefitForm
								title="Seguro de Vida"
								data={details.lifeInsurance}
								form={lifeForm}
								setForm={setLifeForm}
								onSubmit={handleLifeInsuranceUpdate}
								loading={updatingLife}
								partnerOptions={BENEFIT_FORM_PARTNER_OPTIONS}
							/>
							<BenefitForm
								title="DIT"
								data={details.dit}
								form={ditForm}
								setForm={setDitForm}
								onSubmit={handleDitUpdate}
								loading={updatingDit}
								isDit
								partnerOptions={BENEFIT_FORM_PARTNER_OPTIONS}
							/>

							<Section>
								<SectionHeader>
									<div>
										<h3>Observações operacionais</h3>
										<p>
											{observations.length}{" "}
											{observations.length === 1
												? "observação registrada"
												: "observações registradas"}
										</p>
									</div>
								</SectionHeader>
								<ObservationsBody>
									{observations.length ? (
										observations.map(item => (
											<Observation key={item._id}>
												<header>
													<strong>{item.createdByName || "Sistema"}</strong>
													{" · "}
													{dayjs(item.createdAt).format("DD/MM/YYYY HH:mm")}
												</header>
												<p>{item.note}</p>
											</Observation>
										))
									) : (
										<EmptyState>Nenhuma observação registrada.</EmptyState>
									)}
									<Divider />
									<Field>
										Adicionar observação
										<textarea
											value={observation}
											onChange={event => setObservation(event.target.value)}
											placeholder="Digite uma nova observação..."
											maxLength={2000}
											disabled={savingObservation}
										/>
									</Field>
									<SectionActions>
										<PrimaryButton
											type="button"
											onClick={handleObservation}
											disabled={savingObservation}
										>
											{savingObservation ? "Salvando..." : "Salvar observação"}
										</PrimaryButton>
									</SectionActions>
								</ObservationsBody>
							</Section>
						</ModalContent>
					) : (
						<EmptyState>Não foi possível carregar os dados.</EmptyState>
					)}
				</ModalBody>
			</Modal>
		</>
	);
};

export default BenefitUpdateModal;
